import { SearchIcon, SlidersHorizontalIcon, XIcon } from 'lucide-react';
import { shopLabels } from '../../data/shopLabels';
import { plural } from '../../core/format/text';
import { btnSecondary } from '../../ui/styles';
import { FilterPanel, SkinChips } from './ShopFilters';
import { ProductGrid } from './ProductGrid';
import { AdviceTile } from './AdviceTile';
import { ShopEmpty } from './ShopEmpty';
import { ADVICE_SLOT, sorts } from './shopRules';
import type { ShopFilterControl } from './useShopFilters';
type Props = {
  f: ShopFilterControl;
  onOpenFilters: () => void;
};

/** De collectie: filterkolom (desktop), zoeken, sorteren, actieve filters en de grid. */
export function ShopCollection({
  f,
  onOpenFilters
}: Props) {
  const count = f.visible.length;
  return <section className="bg-sand pb-16 pt-6 lg:pb-24 lg:pt-10" aria-label="Collectie" data-id="element-6841" data-mp="2s9cj9161nydq">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-6842" data-mp="2n7vlvfmrqu1w">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-12" data-id="element-6843" data-mp="2i8sgtv6sgntx">
          {/* Desktop: vaste filterkolom */}
          <aside className="hidden lg:col-span-3 lg:block" aria-label="Filters" data-id="element-6844" data-mp="3ecqcsv8fo671">
            <div className="lg:sticky lg:top-28" data-id="element-6845" data-mp="3j48anaf5tqns">
              <FilterPanel skin={f.skin} setSkin={f.setSkin} step={f.step} setStep={f.setStep} brand={f.brand} setBrand={f.setBrand} data-id="element-6846" data-mp="3j4zuxp3b41yr" />
              {f.hasFilters && <button type="button" onClick={f.clear} className="mt-8 inline-flex items-center gap-1.5 text-[13px] text-clay transition-colors duration-150 ease-soft hover:text-ink" data-id="element-6847" data-mp="3acg3b8lfmxnf">
                  <XIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-6848" data-mp="3o2rzbil51gju" />
                  Alle filters wissen
                </button>}
            </div>
          </aside>

          {/* min-w-0: anders rekt het scrollrijtje hieronder de kolom op tot buiten het scherm. */}
          <div className="min-w-0 lg:col-span-9" data-id="element-6849" data-mp="2hmppz1rmjzsd">
            {/* Mobiel: huidtype direct zichtbaar, rest in een lade */}
            <div className="-mx-6 mb-5 flex gap-2 overflow-x-auto px-6 pb-1 [scrollbar-width:none] lg:hidden [&::-webkit-scrollbar]:hidden" data-id="element-6850" data-mp="1xl4ny8kaun7r">
              <SkinChips skin={f.skin} setSkin={f.setSkin} data-id="element-6851" data-mp="1tjt3sik72u6y" />
            </div>

            <div className="flex items-center gap-4" data-id="element-6852" data-mp="1oqzztwwjvpvx">
              <div className="relative flex-1" data-id="element-6853" data-mp="1jt7vu4imsdbd">
                <SearchIcon className="pointer-events-none absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" strokeWidth={1.5} aria-hidden="true" data-id="element-6854" data-mp="1jseyfu5kb05w" />
                <label htmlFor="shop-search" className="sr-only" data-id="element-6855" data-mp="19va7zi4ooija">
                  Zoek in de collectie
                </label>
                <input id="shop-search" type="search" value={f.query} onChange={e => f.setQuery(e.target.value)} placeholder="Zoek op product, ingrediënt of huidtype" className="w-full border-b border-line bg-transparent py-3 pl-7 pr-10 text-[16px] text-ink sm:text-[15px] placeholder:text-muted focus:border-ink focus:outline-none [&::-webkit-search-cancel-button]:hidden" data-id="element-6856" data-mp="15ugk2or8r3qe" />
                {f.query && <button type="button" onClick={() => {
                f.setQuery('');
                // Focus terug in het veld: de knop verdwijnt, anders valt de focus naar het begin van de pagina.
                document.getElementById('shop-search')?.focus();
              }} aria-label="Zoekopdracht wissen" className="absolute -right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center text-muted transition-colors duration-150 ease-soft hover:text-ink" data-id="element-6857" data-mp="37ow91eo0sanp">
                    <XIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-6858" data-mp="2xqowd80rkeri" />
                  </button>}
              </div>
              <button type="button" onClick={onOpenFilters} aria-haspopup="dialog" className="inline-flex min-h-[44px] shrink-0 items-center gap-2 rounded-full border border-ink/15 px-4 text-[14px] text-ink transition-colors duration-150 ease-soft active:border-ink lg:hidden" data-id="element-6859" data-mp="d1izuctmhjdd">
                <SlidersHorizontalIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6860" data-mp="gkhrxx8vz589" />
                Filters
                {f.extraFilterCount > 0 && <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-ink px-1 text-[12px] text-shell" data-id="element-6861" data-mp="uig4qy7k6tln">
                    <span aria-hidden="true" data-id="element-6862" data-mp="149swzqx9rls7">{f.extraFilterCount}</span>
                    <span className="sr-only" data-id="element-6863" data-mp="14c5a9qs317yx">, {f.extraFilterCount} actief</span>
                  </span>}
              </button>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-4 border-b border-line pb-4" data-id="element-6864" data-mp="1dbsbocktadrz">
              <p className="text-[14px] text-muted" aria-live="polite" data-id="element-6865" data-mp="19aqdlz2ms7rl">
                <span aria-hidden="true" data-id="element-6866" data-mp="1rx7o0k9r136i">{plural(count, 'product', 'producten')}</span>
                <span className="sr-only" data-id="element-6867" data-mp="1wx2e4llp9oqi">{shopLabels.results(count)}</span>
              </p>
              <div className="flex items-center gap-2" data-id="element-6868" data-mp="1ix1aiye1w37c">
                <label htmlFor="sort" className="text-[13px] text-muted" data-id="element-6869" data-mp="1iwhvbpfsbsx0">
                  Sorteren
                </label>
                <select id="sort" value={f.sort} onChange={e => {
                const next = sorts.find(s => s === e.target.value);
                if (next) f.setSort(next);
              }} className="min-h-[44px] border-b border-line bg-transparent py-2 text-[16px] text-ink focus:border-ink focus:outline-none sm:min-h-0 sm:py-1 sm:text-[14px]" data-id="element-6870" data-mp="196yawfcfbr01">
                  {sorts.map(s => <option key={s} value={s} data-id="element-6871" data-mp="qagdbhaw8wvv">
                      {s}
                    </option>)}
                </select>
              </div>
            </div>

            {f.activeChips.length > 0 && <ul className="mt-4 flex flex-wrap gap-2" aria-label="Actieve filters" data-id="element-6872" data-mp="2s54ahlsuoky1">
                {f.activeChips.map(c => <li key={c.label} data-id="element-6873" data-mp="2o4ke4kg3plcy">
                    <button type="button" onClick={c.onRemove} className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full bg-blush pl-4 pr-3 text-[13px] text-ink transition-colors duration-150 ease-soft hover:bg-line" data-id="element-6874" data-mp="2j4y1ac46q74o">
                      {c.label}
                      <span className="sr-only" data-id="element-6875" data-mp="36t8wayn79cl1">, filter verwijderen</span>
                      <XIcon className="h-3 w-3" strokeWidth={1.75} aria-hidden="true" data-id="element-6876" data-mp="31vyz2wtkjwmn" />
                    </button>
                  </li>)}
              </ul>}

            {count === 0 ? <ShopEmpty query={f.query} onClear={f.clear} data-id="element-6877" data-mp="1qf91u374if56" /> : <>
                <ProductGrid products={f.shown} insert={count > ADVICE_SLOT ? {
              index: ADVICE_SLOT,
              node: <AdviceTile data-id="element-6879" data-mp="1landnfl8trnh" />
            } : undefined} data-id="element-6878" data-mp="1gdeqf0l8mxfd" />
                {f.hasMore && <div className="mt-14 flex flex-col items-center gap-3 border-t border-line pt-10" data-id="element-6880" data-mp="1q9zwlp8zueks">
                    <p className="text-[14px] text-muted" data-id="element-6881" data-mp="1v1rr2av9t4g7">
                      {f.limit} van {count} producten
                    </p>
                    <button type="button" onClick={f.showMore} className={btnSecondary} data-id="element-6882" data-mp="1z2bl35kjrry4">
                      Toon meer producten
                    </button>
                  </div>}
              </>}
          </div>
        </div>
      </div>
    </section>;
}